import FeatureGates from '@atlaskit/feature-gate-js-client/feature-gates';
import type { NextBestAction } from '@post-office/confluence-next-best-action';
import type { InAppNarrowExposureExperiment } from '@post-office/message-template-contracts/message-template-placement-config';
import type { SerializableRecord } from '@post-office/serializable-record';
import type { EligibleMessage } from '@post-office/shared-contracts/shared-request-middleware-context/context';

import {
	TEMPLATE_XFLOW_MESSAGE_KEY,
	type PostOfficeSideNavMessageKey,
} from './contexts/PostOfficeSideNavMessageProvider';
import {
	NOT_ENROLLED_COHORT,
	RECENT_DOWNGRADE_DISCOUNT_EXPERIMENT,
	RECENT_DOWNGRADE_DISCOUNT_TEMPLATE_ID,
	ROVO_EXTENSION_CTA_TEMPLATE_ID,
} from './experimental-message-list';

export type SideNavMessage = EligibleMessage<SerializableRecord> & {
	inAppNarrowExposureExperiment?: InAppNarrowExposureExperiment;
	explicitEnableRequired?: boolean;
	experimentKey?: string;
	nextBestActionType?: NextBestAction;
	tapReminderTrait?: string;
};

export type SideNavMessageContext = {
	enabledMessages: PostOfficeSideNavMessageKey[];
};

export type DisplayedMessage = {
	message: SideNavMessage;
};

// Apply placement-specific and non-standard experiment eligibility before the generic hook runs,
// preserving the original custom behavior without logging an unintended experiment exposure.
export const filterEligibleMessages = (
	messages: SideNavMessage[],
	context: SideNavMessageContext | null,
	currentRoute: string | undefined,
): SideNavMessage[] =>
	messages.filter((message) => {
		if (message.explicitEnableRequired) {
			const isTemplateXflowMessage = message.experimentKey === TEMPLATE_XFLOW_MESSAGE_KEY;
			const explicitMessageKey = isTemplateXflowMessage
				? TEMPLATE_XFLOW_MESSAGE_KEY
				: message.nextBestActionType;
			const isExplicitlyEnabled =
				explicitMessageKey !== undefined && context?.enabledMessages.includes(explicitMessageKey);

			if (isTemplateXflowMessage) {
				const hasReminderTrait = message.tapReminderTrait === 'remind';

				return hasReminderTrait || isExplicitlyEnabled;
			}

			return isExplicitlyEnabled;
		}

		if (message.messageTemplateId === ROVO_EXTENSION_CTA_TEMPLATE_ID) {
			// Don't show on FPS to avoid collision with existing install CTA (built outside of PO)
			if (currentRoute?.includes('/search')) {
				return false;
			}
		}

		return true;
	});

// Resolve template xflow's explicit enablement and ranking override before either selection path.
export const findExplicitTemplateXflowMessage = (
	messages: SideNavMessage[],
	context: SideNavMessageContext | null,
): SideNavMessage | undefined =>
	context?.enabledMessages.includes(TEMPLATE_XFLOW_MESSAGE_KEY)
		? messages.find((message) => message.experimentKey === TEMPLATE_XFLOW_MESSAGE_KEY)
		: undefined;

// Preserve the original selection behavior while migration gates are off.
export const selectLegacyDisplayedMessage = (
	messages: SideNavMessage[],
	context: SideNavMessageContext | null,
): DisplayedMessage | undefined => {
	if (!messages.length) {
		return;
	}

	const explicitlyEnabledTemplateXflowMessage = findExplicitTemplateXflowMessage(messages, context);

	if (explicitlyEnabledTemplateXflowMessage) {
		return { message: explicitlyEnabledTemplateXflowMessage };
	}

	const [topMessage] = messages;

	// The original discount experiment only records exposure when its message is first in ranking.
	if (topMessage?.messageTemplateId === RECENT_DOWNGRADE_DISCOUNT_TEMPLATE_ID) {
		// oxlint-disable-next-line post-office/no-manual-in-app-experiments -- Legacy selection is retained while migration gates are disabled.
		const recentDowngradeDiscountCohort = FeatureGates.getExperimentValue<string>(
			RECENT_DOWNGRADE_DISCOUNT_EXPERIMENT,
			'cohort',
			NOT_ENROLLED_COHORT,
		);

		if (recentDowngradeDiscountCohort === 'variation') {
			return { message: topMessage };
		}
	}

	const candidateMessages = messages.filter(
		(message) => message.messageTemplateId !== RECENT_DOWNGRADE_DISCOUNT_TEMPLATE_ID,
	);

	return candidateMessages[0] ? { message: candidateMessages[0] } : undefined;
};
